<template>
  <Loading v-if="loading" />
  <template v-else>
    <BasePopup
      v-model="isCancelChangePlanPopupOpen"
      :primary-button-action="() => (isCancelChangePlanPopupOpen = false)"
      :secondary-button-action="() => $emit('close')"
      :title="t('cancel_upgrade_alert_title')"
      :primary-button-text="t('continue_upgrade')"
      :secondary-button-text="t('cancel_upgrade')"
    >
      <template #content>
        <div
          v-html="
            t('cancel_upgrade_alert_description', {
              currentPlan: currentPlanType && getPlanName(currentPlanType),
              selectedPlan: selectedPlanType && getPlanName(selectedPlanType),
            })
          "
        ></div>
      </template>
    </BasePopup>
    <Loading v-if="loading" />
    <Stepper
      v-model="activeStep"
      :max-value="3"
      :show-logo="false"
      :show-stepper="
        (currentStore?.spendingLimit?.activeLimit && hasShopifyPaymentDetails) ||
        account.userFromMarketplace
      "
    >
      <StepperContent :is-visible="activeStep === 1">
        <FullWidthPanel
          v-if="!isCustomSpendingLimitSetupFinished"
          :title="isUpgrade ? t('upgrade_title') : t('downgrade_title')"
        >
          <div class="change-plan">
            <p
              v-if="isUpgrade"
              class="upgrade-text"
              v-html="
                t(isTrial ? 'upgrade_from_trial.subtitle' : 'upgrade.subtitle', {
                  currentPlan: currentPlanType && getPlanName(currentPlanType),
                  selectedPlan: selectedPlanType && getPlanName(selectedPlanType),
                })
              "
            />
            <p
              v-else
              class="upgrade-text"
              v-html="
                t(
                  isTrial ? 'downgrade_from_trial.subtitle' : 'downgrade.subtitle',
                  currentPlanType &&
                    selectedPlanType && {
                      currentPlan: getPlanName(currentPlanType),
                      selectedPlan: getPlanName(selectedPlanType),
                    },
                )
              "
            />
            <div class="price-difference-wrapper" v-if="selectedPlanType">
              <span class="plan-name">{{ getPlanName(selectedPlanType) }} {{ t('plan') }}</span>
              <span class="plan-price">{{ displayPrice }}</span>
            </div>
            <p
              class="upgrade-disclaimer"
              v-if="isUpgrade"
              v-html="
                t(
                  isTrial
                    ? isSelectedPlanYearly
                      ? 'upgrade_from_trial.disclaimer_per_year'
                      : 'upgrade_from_trial.disclaimer_per_month'
                    : 'upgrade.disclaimer',
                  {
                    totalAmount: displayPrice,
                  },
                )
              "
            />
            <p
              class="upgrade-disclaimer"
              v-else
              v-html="
                t(
                  isTrial
                    ? isSelectedPlanYearly
                      ? 'downgrade_from_trial.disclaimer_per_year'
                      : 'downgrade_from_trial.disclaimer_per_month'
                    : 'downgrade.disclaimer',
                  {
                    totalAmount: displayPrice,
                  },
                )
              "
            />
            <div v-if="isDowngradeDisabled" class="error-message">
              <ul class="issue-list">
                <li
                  v-for="({ issue, currentAmount, maxAmount }, index) in downgradeIssues"
                  :key="index"
                  class="issue-list-item"
                >
                  <v-icon size="24px" class="issue-icon"> mdi-information-outline </v-icon>
                  <div>{{ t(snakeCase(issue), { currentAmount, maxAmount }) }}</div>
                </li>
              </ul>
            </div>
            <v-checkbox
              v-if="!isDowngradeDisabled"
              v-model="checkbox"
              color="green"
              hide-details
              :rules="[rules.required]"
              class="terms-checkbox"
            >
              <template v-slot:label>
                <i18n-t tag="p" keypath="Signup.accept" class="terms">
                  <a :href="TERMS_CONDITIONS_URL" target="_blank" class="link">{{ t('terms') }}</a>
                  <a :href="PRIVACY_POLICY_URL" target="_blank" class="link">{{ t('privacy') }}</a>
                </i18n-t>
              </template>
            </v-checkbox>
            <div class="button-wrapper">
              <gs-button @click="navigateBack" icon="mdi-chevron-left" size="large" type="outlined">
                {{ $t('CommonUi.back') }}
              </gs-button>
              <gs-button
                type="primary"
                size="large"
                full-width
                :disabled="!checkbox || changePlanLoading || isDowngradeDisabled"
                :loading="changePlanLoading"
                @click="handlePlanChange()"
              >
                <span v-if="isUpgrade">
                  {{ isTrial ? t('upgrade_from_trial_button_text') : t('upgrade_button_text') }}
                </span>
                <span v-else>
                  {{ isTrial ? t('downgrade_from_trial_button_text') : t('downgrade_button_text') }}
                </span>
              </gs-button>
            </div>
          </div>
        </FullWidthPanel>
      </StepperContent>
      <StepperContent v-if="currentStore" :is-visible="activeStep === 2">
        <FullWidthPanel
          v-if="!isCustomSpendingLimitSetupFinished"
          :title="t('step_two_title', { store: currentStore?.name ?? '' })"
          :description="
            isUpgrade
              ? t('step_two_upgrade_description', {
                  selectedPlan: selectedPlanType && getPlanName(selectedPlanType),
                })
              : t('step_two_downgrade_description', {
                  selectedPlan: selectedPlanType && getPlanName(selectedPlanType),
                })
          "
        >
          <SelectCustomSpendingLimit
            :selected-store="currentStore"
            :selected-plan="selectedPlanType"
            :min-limit="minLimit"
            :limit-types="spendingLimitTypes"
            :success-url="successUrl"
            @back="prevStep"
            @save="nextStep"
          />
        </FullWidthPanel>
      </StepperContent>
      <StepperContent v-if="currentStore" :is-visible="activeStep === 3">
        <FullWidthPanel
          :class="{ success: isCustomSpendingLimitSetupFinished }"
          :icon="isCustomSpendingLimitSetupFinished ? undefined : 'mdi-alert-outline'"
          :title="
            isCustomSpendingLimitSetupFinished
              ? t('step_three_success_title')
              : t('step_three_title')
          "
          :description="
            isCustomSpendingLimitSetupFinished
              ? t('step_three_success_description', {
                  limit: currentStore.spendingLimit?.activeLimit ?? 0,
                  selectedPlan: selectedPlanType && getPlanName(selectedPlanType),
                })
              : t('step_three_description', {
                  store: currentStore?.name ?? '',
                  selectedPlan: selectedPlanType && getPlanName(selectedPlanType),
                })
          "
        >
          <ConfirmCustomSpendingLimit
            v-if="!isCustomSpendingLimitSetupFinished"
            :selected-store="currentStore"
            :selected-plan="selectedPlanType"
            @back="prevStep"
          />
          <CustomSpendingLimitSuccess
            v-else
            :selected-store="currentStore"
            :selected-plan="selectedPlanType"
            @close="close"
          />
        </FullWidthPanel>
      </StepperContent>
    </Stepper>
  </template>
</template>

<script lang="ts">
import {
  NEW_PAID_BUSINESS_SUBSCRIPTION_TYPES,
  PRIVACY_POLICY_URL,
  TERMS_CONDITIONS_URL,
} from '@/helpers/constants'
import { formatCurrencyValueForDisplay, isProductUpgrade } from '@/helpers/pricing'
import { RulesMixin } from '@/helpers/mixins/RulesMixin.vue'
import { type DowngradeIssue, DowngradeMixin } from '@/helpers/mixins/downgradeMixin'
import { Utils } from '@/helpers/mixins/utilsMixin'
import { defineComponent } from 'vue'

import type { SubscriptionItem } from '@/store/subscriptions'
import type { CurrencyCode } from '@/helpers/interfaces'
import type { PropType } from 'vue'
import type { Notification } from '@/store/notification'
import type { Account } from '@/store'
import {
  getSubscriptions,
  updateAccountSubscription,
  getSubscriptionItems,
  getIntegrationSpendingLimitRecommendation,
  type Subscription,
} from '@api/index'
import type {
  NewPaidBusinessSubscriptionType,
  PersonalSubscriptionType,
  TechnicalAndBusinessSubscriptionType,
} from '@/helpers/pricing'
import StepperContent from '@/components/onboarding/StepperContent.vue'
import Stepper from '@/components/onboarding/Stepper.vue'
import CustomSpendingLimitSuccess from '@/components/integration/CustomSpendingLimitSuccess.vue'
import ConfirmCustomSpendingLimit from '@/components/integration/ConfirmCustomSpendingLimit.vue'
import FullWidthPanel from '@/layouts/FullWidthPanel.vue'
import SelectCustomSpendingLimit from '@/components/integration/SelectCustomSpendingLimit.vue'
import BasePopup from '@/components/tools/BasePopup.vue'
import type { Integration } from '@/store/integrations'
import { IntegrationsMixin } from '@/helpers/mixins/integrationsMixin'
import Loading from '@/components/tools/Loading.vue'

export default defineComponent({
  name: 'ChangePlanPopup',
  emits: ['close'],
  mixins: [RulesMixin, DowngradeMixin, Utils, IntegrationsMixin],
  components: {
    StepperContent,
    Stepper,
    FullWidthPanel,
    SelectCustomSpendingLimit,
    CustomSpendingLimitSuccess,
    ConfirmCustomSpendingLimit,
    BasePopup,
    Loading,
  },
  data() {
    return {
      loading: false,
      changePlanLoading: false,
      checkbox: false,
      PRIVACY_POLICY_URL: PRIVACY_POLICY_URL,
      TERMS_CONDITIONS_URL: TERMS_CONDITIONS_URL,
      hasActivePartners: false,
      hasSubscription: false,
      hasStripePaymentDetails: false,
      activeStep: 1,
      isCancelChangePlanPopupOpen: false,
      minLimit: 0,
      spendingLimitTypes: {
        recommended: 0,
        enhanced: 0,
      },
    } as {
      loading: boolean
      changePlanLoading: boolean
      checkbox: boolean
      PRIVACY_POLICY_URL: string
      TERMS_CONDITIONS_URL: string
      hasActivePartners: boolean
      hasSubscription: boolean
      hasStripePaymentDetails: boolean
      activeStep: number
      isCancelChangePlanPopupOpen: boolean
      minLimit: number
      spendingLimitTypes: Record<'recommended' | 'enhanced', number>
    }
  },
  computed: {
    isUpgrade(): boolean {
      if (!this.currentPlanType || !this.selectedPlanType) return false
      return isProductUpgrade(this.currentPlanType, this.selectedPlanType) === 'upgrade'
    },
    isTrial(): boolean {
      return this.$store.getters['isActiveSubscriptionOnTrial']
    },
    isBusinessPlanSelected() {
      return NEW_PAID_BUSINESS_SUBSCRIPTION_TYPES.includes(
        this.selectedPlanType as NewPaidBusinessSubscriptionType,
      )
    },
    downgradeIssues(): DowngradeIssue[] {
      return this.selectedPlanType && this.isBusinessPlanSelected
        ? this.reportDowngradeIssues(this.selectedPlanType as NewPaidBusinessSubscriptionType)
        : []
    },
    isDowngradeDisabled(): boolean {
      if (this.isUpgrade || !this.selectedPlanType) return false

      if (this.isBusinessPlanSelected) {
        return !!this.reportDowngradeIssues(
          this.selectedPlanType as NewPaidBusinessSubscriptionType,
        ).length
      }

      return false
    },
    price(): number {
      const selectedPrice = this.selectedPlanPrice ?? 0
      return this.isSelectedPlanYearly ? selectedPrice * 12 : selectedPrice
    },
    displayPrice(): string {
      return `${this.userCurrencySymbol}${formatCurrencyValueForDisplay(this.price)} ${
        this.userCurrencyCode
      }`
    },
    userCurrencySymbol(): string {
      return this.$store.getters['getUserCurrencySymbol']
    },
    userCurrencyCode(): CurrencyCode {
      return this.$store.getters['getUserCurrencyCode']
    },
    account(): Account {
      return this.$store.getters['getAccount']
    },
    impactWallet(): boolean {
      return this.$store.getters['getImpactWalletFeatureSetting']
    },
    maxCustomEmailTemplateAmount(): number {
      return this.$store.getters['getMaxCustomEmailTemplateAmount']
    },
    maxCustomDomainAmount(): number {
      return this.$store.getters['getMaxCustomDomainAmount']
    },
    isActiveSubscriptionOnTrial(): boolean {
      return this.$store.getters['isActiveSubscriptionOnTrial']
    },
    hasOwnPaidSubscriptionItem(): boolean {
      return this.$store.getters['hasOwnPaidSubscriptionItem']
    },
    hasOwnFreeSubscriptionItem(): boolean {
      return this.$store.getters['hasOwnFreeSubscriptionItem']
    },
    hasPaymentDetails(): (subscriptions: Subscription[]) => boolean {
      return this.$store.getters['hasPaymentDetails']
    },
    hasShopifyPaymentDetails(): boolean {
      return this.$store.getters['hasShopifyPaymentDetails']
    },
    getStripePaymentDetails(): (subscriptions: Subscription[]) => boolean {
      return this.$store.getters['hasStripePaymentDetails']
    },
    integrationList(): Integration[] {
      return this.$store.getters['getIntegrationList']
    },
    userStores(): Integration[] {
      return this.$store.getters['getIntegrationsByPlatform']('shopify')
    },
    currentStore(): Integration | undefined {
      const primaryStore = this.userStores.find((store) => store.spendingLimit?.isPrimaryStore)

      if (this.$route.query.store) {
        return (
          this.userStores.find((store) => store.id === this.$route.query.store) ??
          ({} as Integration)
        )
      } else if (primaryStore) {
        return primaryStore
      } else {
        return this.$store.getters['getLastShopifyIntegration']
      }
    },
    successUrl(): string {
      return `${window.location.href}${window.location.search.length ? '&' : '?'}store=${
        this.currentStore?.id
      }&plan=${this.selectedPlanType}`
    },
    isCustomSpendingLimitSetupFinished(): boolean {
      if (this.$route.query.store) {
        const selectedStore = this.userStores.find((store) => store.id === this.$route.query.store)
        return (
          !selectedStore?.spendingLimit?.pendingLimit && !!selectedStore?.spendingLimit?.activeLimit
        )
      }

      return false
    },
  },
  async created() {
    this.loading = true

    const subscriptions = await getSubscriptions()
    this.hasSubscription = this.hasPaymentDetails(subscriptions)
    this.hasStripePaymentDetails = this.getStripePaymentDetails(subscriptions)

    if (this.maxCustomDomainAmount) await this.setDomainList()
    if (this.maxCustomEmailTemplateAmount) await this.setEmailTemplateList()

    await this.setIntegrations()

    if (this.isCustomSpendingLimitSetupFinished) {
      this.activeStep = 3
      await this.changePlan()
    }

    this.loading = false
  },
  methods: {
    t(key: string, params?: { [key: string]: string | number | undefined }) {
      return this.$t(`ChangePlanPopup.${key}`, params ?? {})
    },
    prevStep() {
      this.activeStep--
    },
    nextStep() {
      this.activeStep++
    },
    navigateBack() {
      this.isCancelChangePlanPopupOpen = true
    },
    close() {
      if (document.documentElement.classList.contains('modal-open')) {
        document.documentElement.classList.remove('modal-open')
      }

      this.$emit('close')
    },
    async changePlan() {
      try {
        if (!this.selectedPlanType) return

        await updateAccountSubscription({ product: this.selectedPlanType })
        this.setSubscriptionItems(await getSubscriptionItems())
        await this.setAccount()
        await this.setRequiredActionAlert()
        await this.setIntegrations()
      } catch (e) {
        const errorMessage = e?.response?.data?.message
        const MINIMUM_SPENDING_LIMIT_VALIDATION_ERROR = 'MINIMUM_SPENDING_LIMIT_VALIDATION_ERROR'

        this.$store.dispatch('notification/notify', {
          text:
            errorMessage === MINIMUM_SPENDING_LIMIT_VALIDATION_ERROR
              ? this.t('change_plan_error')
              : this.t('error'),
          isError: true,
        } as Notification)

        this.close()
        throw new Error(errorMessage)
      }
    },
    async handlePlanChange() {
      this.changePlanLoading = true

      if (!this.hasSubscription && !this.currentStore) {
        return await this.$router.push({
          path: '/checkout',
          query: {
            type: 'subscription',
            plan: this.selectedPlanType,
            total: this.price.toString(),
            trial: this.$route.path === '/select-plan' ? 'true' : 'false',
          },
        })
      }

      if (
        (this.currentStore?.spendingLimit?.activeLimit && this.hasShopifyPaymentDetails) ||
        this.account.userFromMarketplace
      ) {
        try {
          if (!this.selectedPlanType) return

          const currentSpendingLimit = this.currentStore?.spendingLimit?.activeLimit ?? 0

          const { data } = await getIntegrationSpendingLimitRecommendation(
            this.currentStore?.id ?? '',
            this.selectedPlanType,
          )

          this.spendingLimitTypes = {
            recommended: data.recommended,
            enhanced: data.enhanced,
          }
          this.minLimit = data.minimum

          if (currentSpendingLimit < data.minimum) {
            this.nextStep()
          } else {
            await this.changePlan()
            this.$router.replace({
              query: {
                ...this.$route.query,
                store: this.currentStore?.id,
              },
            })
            this.activeStep = 3
          }
        } catch (e) {
          console.error(e?.message)
          this.$store.dispatch('notification/notify', {
            text: this.t('error'),
            isError: true,
          } as Notification)
        } finally {
          this.changePlanLoading = false
        }
      } else {
        try {
          if (!this.selectedPlanType) return

          await this.changePlan()

          this.$store.dispatch('notification/notify', {
            text: this.t('plan_updated'),
          } as Notification)

          if (this.$route.query.tab) {
            await this.$router.push({ path: '/account', query: { tab: this.$route.query.tab } })
          } else {
            await this.$router.back()
          }
        } catch (e) {
          console.error(e?.message)
          this.$store.dispatch('notification/notify', {
            text: this.t('error'),
            isError: true,
          } as Notification)
        } finally {
          this.changePlanLoading = false
          this.close()
        }
      }
    },
    setAccount(): Promise<void> {
      return this.$store.dispatch('setAccount')
    },
    setDomainList() {
      return this.$store.dispatch('setDomainList')
    },
    setEmailTemplateList() {
      return this.$store.dispatch('setEmailTemplateList')
    },
    setRequiredActionAlert(): Promise<void> {
      return this.$store.dispatch('setRequiredActionAlert')
    },
    setSubscriptionItems(items: SubscriptionItem[]) {
      return this.$store.commit('setSubscriptionItems', items)
    },
  },
  props: {
    currentPlanType: {
      type: String as PropType<TechnicalAndBusinessSubscriptionType>,
    },
    selectedPlanType: {
      type: String as PropType<NewPaidBusinessSubscriptionType | PersonalSubscriptionType>,
    },
    selectedPlanPrice: {
      type: Number,
    },
    isCurrentPlanYearly: {
      type: Boolean,
    },
    isSelectedPlanYearly: {
      type: Boolean,
    },
  },
})
</script>

<style lang="scss" scoped>
@import '~vuetify/settings';

:deep(.step-indicator-list) {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
}

:deep(.stepper-items) {
  margin-top: 70px;
}

.upgrade-text {
  font-size: 20px;
  font-weight: normal;
  line-height: 24px;
  margin-top: 15px;
  margin-bottom: 24px;
  text-align: left;
}

.upgrade-text:deep(span) {
  background: var(--ui-beige);
  border-radius: 8px;
  line-height: 24px;
  padding: 4px 6px;
  font-weight: 700;
  white-space: nowrap;
}

.price-difference-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  gap: 16px;
  padding: 8px;
  border-radius: var(--border-radius-big);
  background-color: rgba(71, 209, 143, 0.1);
  margin-bottom: 24px;

  @media #{map-get($display-breakpoints, 'md-and-up')} {
    flex-direction: row;
    gap: 0;
  }

  .plan-name {
    font-size: 20px;
    font-weight: 700;
    line-height: 24px;
  }

  .plan-price {
    font-size: 32px;
    font-weight: 700;
    line-height: 39px;
  }
}

.upgrade-disclaimer {
  font-size: 20px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
}

.terms-checkbox {
  width: 100%;
  padding: 0;
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  .terms {
    color: var(--font-color-primary);
    cursor: pointer;
    font-size: 18px;
    font-weight: 700;
    line-height: 22px;
    flex-wrap: wrap;
    margin-bottom: 0;
    margin-left: 8px;
  }
}

.link {
  margin-left: 3px;
  margin-right: 3px;
  color: var(--ui-green);
  font-weight: bold;
}

.error-message {
  width: 100%;
  padding: 8px;
  background: var(--ui-red);
  margin-bottom: 24px;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
}

.issue-list {
  list-style-type: none;
  padding-left: 0;
}

.issue-list-item {
  position: relative;
  line-height: 20px;
  font-size: 16px;
  font-weight: 700;
  color: var(--ui-white);
  text-align: left;
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-bottom: 15px;
}

.issue-icon {
  color: inherit;
  font-size: inherit;
  padding: 4px;
}

.error-message .issue-list-item:last-child {
  margin: 0;
}

.button-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.success {
  :deep(.panel__title) {
    color: var(--ui-green);
  }
}
</style>
